'use client';

import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'motion/react';
import { Children, cloneElement, useEffect, useRef, useMemo, useState } from 'react';
import { FiHome, FiBook, FiSend, FiUser, FiBookOpen } from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import './App.css';

function DockItem({ children, className = '', onClick, mouseX, spring, distance, magnification, baseItemSize }) {
  const ref = useRef(null);

  const mouseDistance = useTransform(mouseX, val => {
    const rect = ref.current?.getBoundingClientRect() ?? { x: 0, width: baseItemSize };
    return val - rect.x - baseItemSize / 2;
  });

  const targetSize = useTransform(mouseDistance, [-distance, 0, distance], [baseItemSize, magnification, baseItemSize]);
  const size = useSpring(targetSize, spring);

  return (
    <motion.div
      ref={ref}
      style={{ width: size, height: size }}
      onClick={onClick}
      className={`dock-item ${className}`}
      tabIndex={0}
      role="button"
      aria-haspopup="true"
    >
      {Children.map(children, child => cloneElement(child))}
    </motion.div>
  );
}

function DockLabel({ children, className = '', isHovered }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const unsubscribe = isHovered.on('change', latest => setIsVisible(latest === 1));
    return () => unsubscribe();
  }, [isHovered]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: -10 }}
          exit={{ opacity: 0, y: 0 }}
          transition={{ duration: 0.2 }}
          className={`dock-label ${className}`}
          role="tooltip"
          style={{ x: '-50%' }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DockIcon({ children, className = '' }) {
  return <div className={`dock-icon ${className}`}>{children}</div>;
}

export default function Dock({
  items = [],
  className = '',
  spring = { mass: 0.1, stiffness: 150, damping: 12 },
  magnification = 70,
  distance = 200,
  panelHeight = 68,
  dockHeight = 256,
  baseItemSize = 50
}) {
  const navigate = useNavigate();
  const mouseX = useMotionValue(Infinity);
  const isHovered = useMotionValue(0);

  const maxHeight = useMemo(() => Math.max(dockHeight, magnification + magnification / 2 + 4), [magnification, dockHeight]);
  const heightRow = useTransform(isHovered, [0, 1], [panelHeight, maxHeight]);
  const height = useSpring(heightRow, spring);

  // Scroll helper with offset
  const scrollToSection = (hash) => {
    const element = document.querySelector(hash);
    if (element) {
      const yOffset = -80; // adjust for dock height
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Attach navigation to items
  const navItems = items.map(item => ({
    ...item,
    onClick: () => {
      if (item.href.startsWith('#')) {
        // Scroll within the same page
        scrollToSection(item.href);
      } else if (item.href.includes('/#')) {
        // Navigate to home page with hash
        navigate(item.href.split('#')[0]);
        setTimeout(() => scrollToSection(`#${item.href.split('#')[1]}`), 400); // delay for render
      } else {
        navigate(item.href); // normal page navigation
      }
    }
  }));

  return (
    <motion.div style={{ height, scrollbarWidth: 'none' }} className="dock-outer">
      <motion.div
        onMouseMove={({ pageX }) => {
          isHovered.set(1);
          mouseX.set(pageX);
        }}
        onMouseLeave={() => {
          isHovered.set(0);
          mouseX.set(Infinity);
        }}
        className={`dock-panel ${className}`}
        style={{ height: panelHeight }}
        role="toolbar"
        aria-label="Application dock"
      >
        {navItems.map((item, index) => (
          <DockItem
            key={index}
            onClick={item.onClick}
            className={item.className}
            mouseX={mouseX}
            spring={spring}
            distance={distance}
            magnification={magnification}
            baseItemSize={baseItemSize}
          >
            <DockIcon>{item.icon}</DockIcon>
            <DockLabel isHovered={isHovered}>{item.label}</DockLabel>
          </DockItem>
        ))}
      </motion.div>
    </motion.div>
  );
}

// Example usage
export const dockItems = [
  { icon: <FiHome />, label: 'Home', href: '#home' },
  { icon: <FiBook />, label: 'Projects', href: '#projects' },
  { icon: <FiBookOpen />, label: 'Case Studies', href: '#casestudies' },
  { icon: <FiUser />, label: 'About', href: '#about' },
  { icon: <FiSend />, label: 'Contact', href: '#contact' }
];

export const dockItemsOuter = [
  { icon: <FiHome />, label: 'Home', href: '/#home' },
  { icon: <FiBook />, label: 'Projects', href: '/#projects' },
  { icon: <FiBookOpen />, label: 'Case Studies', href: '/#casestudies' },
  { icon: <FiUser />, label: 'About', href: '/#about' },
  { icon: <FiSend />, label: 'Contact', href: '/#contact' }
];
